import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { ApiError } from '@/api/client.types';
import { getKuduUserId } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';
import { ArrowRight, Calendar, CheckCircle, Pencil } from 'lucide-react';
import {
  UNSAFE_DataRouterContext,
  useBlocker,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router';
import { Button } from '@/components/Button/Button';
import { Card } from '@/components/Card/Card';
import { AnnotationLayer } from '@/features/annotations/AnnotationLayer';
import { AnnotationsSidebar } from '@/features/annotations/AnnotationsSidebar';
import { useKuduAnnotationService } from '@/features/annotations/annotationService';
import {
  getAnnotationContainerId,
  getAnnotationHighlightId,
  type AnnotationTextColor,
  type AnnotationReference,
  type AnnotationSidebarGroup,
} from '@/features/annotations/annotationTypes';
import { useAnnotationAvailability } from '@/features/annotations/useAnnotationAvailability';
import { useAnnotations } from '@/features/annotations/useAnnotations';
import { extractPlainText } from '@/features/annotations/text/extractPlainText';
import { resolveTextQuote } from '@/features/annotations/text/textQuoteResolver';
import { getChapterImageUrls } from '@/features/chat/chapterImageUrls';
import { ChatPanel } from '@/features/chat/ChatPanel';
import { ChatbotAvatar } from '@/features/chat/avatar/ChatbotAvatar';
import { useAvatarState } from '@/features/chat/avatar/useAvatarState';
import { useKuduChatService } from '@/features/chat/chatService';
import { useChatAvailability } from '@/features/chat/useChatAvailability';
import { useChatSession } from '@/features/chat/useChatSession';
import { ClassStatsSummary } from '@/features/content/stats/ClassStatsSummary';
import { InstructorStatsToolbar } from '@/features/content/stats/InstructorStatsToolbar';
import { RegradeQuestionsDialog } from '@/features/content/stats/RegradeQuestionsDialog';
import { ScoreSummaryBanner } from '@/features/content/stats/ScoreSummaryBanner';
import { useDownloadDetailedScores } from '@/features/content/stats/useDownloadDetailedScores';
import { useUpdateStats } from '@/features/content/stats/useUpdateStats';
import {
  isInstructionalRole,
  useUserRole,
} from '@/hooks/useUserRole';
import { ControlPanelSummary } from '@/features/content/control-panel/ControlPanelSummary';
import {
  QuestionSection,
  findQuestionEditButton,
} from '@/features/questions/QuestionSection';
import type {
  NormalizedQuestionData,
  NormalizedQuestionSectionData,
} from '@/features/questions/questionNormalizer';
import { resolveImageSizeOverrideForSurface } from '@/features/courses/courseDisplaySettings';
import { useCourse } from '@/features/courses/useCourse';

import {
  isLessonContentShown,
  isQuestionSectionShown,
} from './chapterVisibility';
import { CollapsibleUnit } from './CollapsibleUnit';
import { ContentUnit } from './ContentUnit';
import {
  timelineHasHiddenContent,
  useComponentTimeline,
} from './hooks/useComponentTimeline';
import { useReaderTimeline } from './hooks/useReaderTimeline';
import { useStudentView } from '@/features/studentView/StudentViewProvider';
import {
  ContentEditModeProvider,
  useContentEditModeController,
} from './editor/ContentEditModeProvider';
import { EditableUnitFrame } from './editor/EditableUnitFrame';
import { DividerInsertButton } from './editor/DividerInsertButton';
import { AddContentPicker } from './editor/AddContentPicker';
import { ContentUnitEditMode } from './editor/ContentUnitEditMode';
import { ImageSizeOverrideProvider } from './imageSizeOverrideContext';
import type {
  AddableContentType,
  EditableContentUnit,
  EditableQuestion,
  QuestionSectionKind,
} from './editor/editorTypes';
import { createEditorState } from './editor/editorNormalizer';
import { editableContentUnitToDisplayUnit } from './editor/editableContentUnitDisplayAdapter';
import { QuestionEditDialog } from './editor/QuestionEditDialog';
import {
  areHomeworkEditsRestricted,
  arePreLectureEditsRestricted,
} from './editor/questionEditRestrictions';
import { useSaveComponent } from './editor/useSaveComponent';
import { useNavigationPanelState } from './NavigationPanelState';
import { NavigationPanel } from './NavigationPanel';
import { KaiCoachModulePage } from '@/features/kaiCoach/KaiCoachModulePage';
import {
  isThreadOpen,
  normalizeCoachSection,
} from '@/features/kaiCoach/kaiCoachContent';
import { getKaiCoachSection } from '@/features/kaiCoach/kaiCoachSection';

import { useComponent } from './useComponent';
import { useFloatingDockClearance } from './useFloatingDockClearance';
import {
  type NormalizedContentUnit,
  type NormalizedSection,
  getContentUnitAnchorId,
  QUESTION_SECTION_ID,
  type TocEntry,
  VIDEO_SECTION_ID,
} from './useComponent';
import { VideoSection } from './video/VideoSection';
import {
  CopySectionDialog,
  type CopySectionSuccess,
} from './sectionActions/CopySectionDialog';
import {
  CopyQuestionDialog,
  type CopyQuestionSuccess,
} from './questionActions/CopyQuestionDialog';
import {
  getSectionUnits,
} from './sectionActions/groupUnitsIntoSections';
import type { ContentSection } from './sectionActions/sectionTypes';
import { ChapterActionsMenu } from './ChapterActionsMenu';
import { ExportChapterPdfDialog } from './pdfExport/ExportChapterPdfDialog';
import { getPrintableChapter } from './pdfExport/printableChapter';
import styles from './ContentPage.module.css';

const UNSAVED_CHANGES_MESSAGE =
  'Discard unsaved changes? Your edits will be lost.';

// An answer from the server carries the server's own text, which never
// reaches the page (docs/engineering/frontend.md): a refusal, and any other
// failure the server answered, get words of the page's own. What it says
// before them ("Unable to load this content.", "Save failed.") gives the
// context. A message the page made itself has no status and is shown.
function getErrorMessage(error: ApiError) {
  if (error.kind === 'permission') {
    return 'The request was refused. Reload the page and try again.';
  }
  if (typeof error.status === 'number') {
    return 'Something went wrong on our side. Try again in a moment.';
  }
  return error.message || 'Unable to load this content right now.';
}

function formatScheduledDate(date: Date) {
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

function formatDueCardDate(date: Date) {
  const parts = new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    month: 'short',
    year: 'numeric',
  }).formatToParts(date);
  const partMap = new Map(parts.map((part) => [part.type, part.value]));
  const dayPeriod = partMap.get('dayPeriod');
  const time = [
    partMap.get('hour'),
    partMap.get('minute') ? `:${partMap.get('minute')}` : '',
    dayPeriod ? ` ${dayPeriod}` : '',
  ].join('');

  return `${partMap.get('month')} ${partMap.get('day')}, ${partMap.get('year')} · ${time}`;
}

function getQuestionSectionDueStatus(
  kind: 'homework' | 'preLecture',
  timeline: ReturnType<typeof useComponentTimeline>,
) {
  if (kind === 'preLecture') {
    if (!timeline.isPreLectureVisible && timeline.preLectureStartDate) {
      return `Pre-lecture opens ${formatScheduledDate(timeline.preLectureStartDate)}`;
    }

    if (timeline.preLectureEndDate) {
      return timeline.isPreLectureClosed
        ? `Pre-lecture closed ${formatScheduledDate(timeline.preLectureEndDate)}`
        : `Pre-lecture due: ${formatScheduledDate(timeline.preLectureEndDate)}`;
    }

    return timeline.isPreLectureClosed
      ? 'Pre-lecture is closed.'
      : timeline.isPreLectureOpen
        ? 'Pre-lecture is open.'
        : 'Pre-lecture availability is not scheduled yet.';
  }

  if (!timeline.isHomeworkVisible && timeline.homeworkStartDate) {
    return `Homework opens ${formatScheduledDate(timeline.homeworkStartDate)}`;
  }

  if (timeline.homeworkEndDate) {
    return timeline.isHomeworkClosed
      ? `Homework closed ${formatScheduledDate(timeline.homeworkEndDate)}`
      : `Homework due: ${formatScheduledDate(timeline.homeworkEndDate)}`;
  }

  return timeline.isHomeworkClosed
    ? 'Homework is closed.'
    : timeline.isHomeworkOpen
      ? 'Homework is open.'
      : 'Homework availability is not scheduled yet.';
}

type RenderableUnit =
  | {
      kind: 'group';
      summaryLabel: string;
      units: NormalizedContentUnit[];
    }
  | {
      kind: 'single';
      unit: NormalizedContentUnit;
    };

function getGroupedSummaryLabel(units: NormalizedContentUnit[]) {
  const firstUnit = units[0];

  if (!firstUnit) {
    return 'Additional content';
  }

  return firstUnit.heading ? firstUnit.summaryLabel : 'Additional content';
}

function createRenderableUnits(units: NormalizedContentUnit[]): RenderableUnit[] {
  const renderableUnits: RenderableUnit[] = [];
  let currentIndex = 0;

  while (currentIndex < units.length) {
    const currentUnit = units[currentIndex];

    if (!currentUnit?.collapseByDefault) {
      renderableUnits.push({
        kind: 'single',
        unit: currentUnit,
      });
      currentIndex += 1;
      continue;
    }

    const groupedUnits: NormalizedContentUnit[] = [currentUnit];
    let nextIndex = currentIndex + 1;

    while (nextIndex < units.length && units[nextIndex]?.collapseByDefault) {
      groupedUnits.push(units[nextIndex]);
      nextIndex += 1;
    }

    if (groupedUnits.length === 1) {
      renderableUnits.push({
        kind: 'single',
        unit: currentUnit,
      });
    } else {
      renderableUnits.push({
        kind: 'group',
        summaryLabel: getGroupedSummaryLabel(groupedUnits),
        units: groupedUnits,
      });
    }

    currentIndex = nextIndex;
  }

  return renderableUnits;
}

function createEmptyEditorDisplayUnit(
  unit: EditableContentUnit,
  unitPosition: number,
): NormalizedContentUnit {
  const heading = unit.heading?.trim() || undefined;

  return {
    caption: unit.caption?.trim() || undefined,
    collapseByDefault: unit.collapseByDefault,
    disableAutoNumbering: unit.disableAutoNumbering,
    heading,
    html: '<p></p>',
    id: unit.id,
    kind: 'text',
    summaryLabel: heading ?? `Content item ${unitPosition}`,
  };
}

function createEditModeSections(
  contentUnits: EditableContentUnit[],
  fallbackSections: NormalizedSection[],
): NormalizedSection[] {
  const sections = new Map<number, NormalizedSection>();
  let headingNumber = 0;

  contentUnits.forEach((unit, index) => {
    const sectionIndex = unit.sourceSectionIndex;
    const section =
      sections.get(sectionIndex) ??
      ({
        id:
          fallbackSections[sectionIndex]?.id ??
          unit.sourceSectionId ??
          `edit-section-${sectionIndex}`,
        units: [],
      } satisfies NormalizedSection);
    const displayUnit =
      editableContentUnitToDisplayUnit(unit, index + 1, () => {
        headingNumber += 1;
        return headingNumber;
      }) ?? createEmptyEditorDisplayUnit(unit, index + 1);

    section.units.push(displayUnit);
    sections.set(sectionIndex, section);
  });

  return Array.from(sections.entries())
    .sort(([leftIndex], [rightIndex]) => leftIndex - rightIndex)
    .map(([, section]) => section);
}

function createEmptyEditorQuestionSection(
  kind: QuestionSectionKind,
): NormalizedQuestionSectionData {
  const isPreLecture = kind === 'preLecture';
  const id = isPreLecture
    ? 'editor-empty-pre-lecture-questions'
    : 'editor-empty-homework-questions';

  return {
    earnedPoints: undefined,
    hasAggregateStats: false,
    heading: isPreLecture ? 'Pre-Lecture Questions' : 'Homework Questions',
    id,
    isInstructorView: true,
    isPastDue: false,
    kind,
    possiblePoints: 0,
    questionCount: 0,
    questionSectionId: id,
    questions: [],
    rawQuestionStats: [],
    sectionNumAttempts: 0,
    sectionStatsId: id,
    sectionTotalScore: 0,
    solutionsVisible: false,
  };
}

function getSectionRenderKey(section: NormalizedSection) {
  return `content-section:${section.id}`;
}

function getCollapsedGroupRenderKey(units: NormalizedContentUnit[]) {
  return `collapsed-group:${units.map((unit) => unit.id).join(':')}`;
}

function getUnitTypeLabel(unit: NormalizedContentUnit) {
  if (unit.kind === 'image') {
    return 'Image';
  }

  if (unit.kind === 'iframe') {
    return 'Iframe';
  }

  if (unit.kind === 'inlineVideo') {
    return 'Video';
  }

  return 'Text';
}

function isEditableExampleHeading(heading: string | undefined) {
  return /^example\b/i.test(heading?.trim() ?? '');
}

function isEditableTocUnit(unit: EditableContentUnit) {
  return Boolean(unit.heading?.trim()) && !unit.collapseByDefault;
}

function getEditableNavSectionId(unit: EditableContentUnit) {
  return unit.sourceSectionId || `section-${unit.sourceSectionIndex}`;
}

function createEditModeTocEntries(
  contentUnits: EditableContentUnit[],
  videoIds: string[],
  questionCount: number,
): TocEntry[] {
  const tocEntries: TocEntry[] = [];
  let contentHeadingNumber = 0;

  if (videoIds.length > 0) {
    tocEntries.push({
      id: VIDEO_SECTION_ID,
      label: 'Video',
      level: 'normal',
      type: 'video',
    });
  }

  contentUnits.forEach((unit) => {
    const heading = unit.heading?.trim();

    if (!heading || unit.collapseByDefault) {
      return;
    }

    const shouldNumber =
      !unit.disableAutoNumbering && !isEditableExampleHeading(heading);

    if (shouldNumber) {
      contentHeadingNumber += 1;
    }

    tocEntries.push({
      id: getContentUnitAnchorId(unit.id),
      label: heading,
      level: isEditableExampleHeading(heading) ? 'example' : 'normal',
      numberedLabel: shouldNumber
        ? `${contentHeadingNumber}. ${heading}`
        : undefined,
      type: 'content',
    });
  });

  if (questionCount > 0) {
    tocEntries.push({
      id: QUESTION_SECTION_ID,
      label: 'Questions',
      level: 'normal',
      type: 'question',
    });
  }

  return tocEntries;
}

interface NavReorderEntry {
  childUnitIds: string[];
  id: string;
  sectionId: string;
  unitId: string;
}

interface NavReorderSection {
  entries: NavReorderEntry[];
  id: string;
}

function createNavReorderSections(
  contentUnits: EditableContentUnit[],
): NavReorderSection[] {
  const sections = new Map<string, NavReorderSection>();

  contentUnits.forEach((unit, index) => {
    if (!isEditableTocUnit(unit)) {
      return;
    }

    const sectionId = getEditableNavSectionId(unit);
    const childUnitIds: string[] = [];
    let nextIndex = index + 1;

    while (nextIndex < contentUnits.length) {
      const nextUnit = contentUnits[nextIndex];

      if (!nextUnit || getEditableNavSectionId(nextUnit) !== sectionId) {
        break;
      }

      if (isEditableTocUnit(nextUnit)) {
        break;
      }

      childUnitIds.push(nextUnit.id);
      nextIndex += 1;
    }

    const section = sections.get(sectionId) ?? {
      entries: [],
      id: sectionId,
    };

    section.entries.push({
      childUnitIds,
      id: getContentUnitAnchorId(unit.id),
      sectionId,
      unitId: unit.id,
    });
    sections.set(sectionId, section);
  });

  return Array.from(sections.values());
}

function createContentActionSections(
  contentUnits: EditableContentUnit[],
): ContentSection[] {
  const sections: ContentSection[] = [];

  contentUnits.forEach((unit, index) => {
    if (!isEditableTocUnit(unit)) {
      return;
    }

    const sectionId = getEditableNavSectionId(unit);
    const bodyUnits: EditableContentUnit[] = [];
    const bodyUnitIndices: number[] = [];
    let nextIndex = index + 1;

    while (nextIndex < contentUnits.length) {
      const nextUnit = contentUnits[nextIndex];

      if (!nextUnit || getEditableNavSectionId(nextUnit) !== sectionId) {
        break;
      }

      if (isEditableTocUnit(nextUnit)) {
        break;
      }

      bodyUnits.push(nextUnit);
      bodyUnitIndices.push(nextIndex);
      nextIndex += 1;
    }

    sections.push({
      bodyUnitIndices,
      bodyUnits,
      headerUnit: unit,
      headerUnitIndex: index,
      id: unit.id,
      title: unit.heading?.trim() || 'Untitled section',
    });
  });

  return sections;
}

function createSingleUnitCopySection(
  unit: EditableContentUnit,
  index: number,
): ContentSection {
  return {
    bodyUnitIndices: [],
    bodyUnits: [],
    headerUnit: unit,
    headerUnitIndex: index,
    id: unit.id,
    title: unit.heading?.trim() || `Content item ${index + 1}`,
  };
}

function getDragPreviewDescription(unit: NormalizedContentUnit) {
  if (unit.kind === 'text') {
    return extractPlainText(unit.html).text.trim();
  }

  if (unit.kind === 'image') {
    return unit.caption ?? unit.alt;
  }

  if (unit.kind === 'iframe') {
    return unit.title || unit.src;
  }

  if (unit.kind === 'applet') {
    return unit.title;
  }

  if (unit.kind === 'table') {
    return unit.caption ?? 'Table';
  }

  return unit.title || unit.videoId;
}

function DragPreview({
  unit,
  width,
}: {
  unit: NormalizedContentUnit;
  width: number | null;
}) {
  const title = unit.numberedHeading ?? unit.heading ?? unit.summaryLabel;
  const description = getDragPreviewDescription(unit);

  return (
    <div
      className={styles.dragPreview}
      style={width ? { width } : undefined}
    >
      <span className={styles.dragPreviewBadge}>{getUnitTypeLabel(unit)}</span>
      <p className={styles.dragPreviewTitle}>{title}</p>
      {description ? (
        <p className={styles.dragPreviewDescription}>{description}</p>
      ) : null}
    </div>
  );
}

function waitForNextFrame() {
  return new Promise<void>((resolve) => {
    if (typeof window === 'undefined') {
      resolve();
      return;
    }

    window.requestAnimationFrame(() => resolve());
  });
}

type IdleCallbackWindow = Window & {
  requestIdleCallback?: (
    callback: () => void,
    options?: { timeout?: number },
  ) => number;
};

function scheduleIdleWork(callback: () => void) {
  if (typeof window === 'undefined') {
    callback();
    return;
  }

  const idleWindow = window as IdleCallbackWindow;

  if (typeof idleWindow.requestIdleCallback === 'function') {
    idleWindow.requestIdleCallback(callback, { timeout: 1000 });
    return;
  }

  window.setTimeout(callback, 0);
}

function ContentEditRouterBlocker({
  onDiscard,
  shouldBlock,
}: {
  onDiscard: () => void;
  shouldBlock: boolean;
}) {
  const onDiscardRef = useRef(onDiscard);

  useEffect(() => {
    onDiscardRef.current = onDiscard;
  }, [onDiscard]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (!shouldBlock) {
      return false;
    }

    const isLeavingCurrentLocation =
      currentLocation.pathname !== nextLocation.pathname ||
      currentLocation.search !== nextLocation.search ||
      currentLocation.hash !== nextLocation.hash;

    if (!isLeavingCurrentLocation) {
      return false;
    }

    if (window.confirm(UNSAVED_CHANGES_MESSAGE)) {
      onDiscardRef.current();
      return false;
    }

    return true;
  });

  useEffect(() => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [blocker]);

  return null;
}

function ContentEditNavigationGuard({
  onDiscard,
  shouldBlock,
}: {
  onDiscard: () => void;
  shouldBlock: boolean;
}) {
  const dataRouterContext = useContext(UNSAFE_DataRouterContext);

  useEffect(() => {
    if (!shouldBlock) {
      return undefined;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = UNSAVED_CHANGES_MESSAGE;
      return UNSAVED_CHANGES_MESSAGE;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldBlock]);

  if (!dataRouterContext) {
    return null;
  }

  return (
    <ContentEditRouterBlocker
      onDiscard={onDiscard}
      shouldBlock={shouldBlock}
    />
  );
}

/**
 * The anchor of the question set at this position on the page. The first
 * keeps the id that the contents' Questions entry and older links use.
 */
function getQuestionSectionAnchorId(index: number) {
  return index === 0 ? QUESTION_SECTION_ID : `${QUESTION_SECTION_ID}-${index + 1}`;
}

function getQuestionCountLabel(questionCount: number) {
  return `${questionCount} question${questionCount === 1 ? '' : 's'}`;
}

function getAssignmentDisplayName(kind: 'homework' | 'preLecture') {
  return kind === 'preLecture' ? 'Pre-lecture' : 'Homework';
}

function getAssignmentDueDate(
  kind: 'homework' | 'preLecture',
  timeline: ReturnType<typeof useComponentTimeline>,
) {
  return kind === 'preLecture'
    ? timeline.preLectureEndDate
    : timeline.homeworkEndDate;
}

function sortAssignmentBlocks(
  left: AssignmentDueBlock,
  right: AssignmentDueBlock,
) {
  // What can still be answered comes first.
  const isLeftDone = left.isComplete || left.isClosed;
  const isRightDone = right.isComplete || right.isClosed;

  if (isLeftDone !== isRightDone) {
    return isLeftDone ? 1 : -1;
  }

  const dueDelta = left.dueDate.getTime() - right.dueDate.getTime();

  if (dueDelta !== 0) {
    return dueDelta;
  }

  if (left.kind === right.kind) {
    return 0;
  }

  return left.kind === 'preLecture' ? -1 : 1;
}

/**
 * Whether the student has put an answer into this question — which is what the
 * due-date card counts, not whether the answer was right.
 *
 * Written as an exhaustive switch on purpose. This was a chain of `if`s ending
 * in `return false`, so when select-all was added it silently became a question
 * that could never be completed: a student who had answered everything was told
 * one of three was done, and the assignment never read as finished. A new
 * question kind now fails to compile here until it says what answered means.
 */
function isQuestionCompleted(question: NormalizedQuestionData): boolean {
  switch (question.kind) {
    case 'aiUpload':
      return Boolean(question.submittedFileUrl);
    case 'exactAnswer':
    case 'numerical':
      return question.inputValue.trim().length > 0;
    case 'multiPart':
      return (
        question.parts.length > 0 && question.parts.every(isQuestionCompleted)
      );
    case 'multipleChoice':
      return question.selectedIndex !== undefined && question.selectedIndex >= 0;
    case 'selectAll':
      // Any selection counts. Grading is all-or-nothing, but this measures
      // effort, not correctness — the same way a wrong multiple-choice pick
      // counts as answered.
      return question.selectedIndices.length > 0;
    default: {
      const unhandledKind: never = question;
      void unhandledKind;
      return false;
    }
  }
}

interface AssignmentDueBlock {
  /** The anchor of its question set on the page; null while the set is not shown. */
  anchorId: string | null;
  completedQuestions: number;
  dueDate: Date;
  id: string;
  isClosed: boolean;
  isComplete: boolean;
  kind: 'homework' | 'preLecture';
  label: string;
  totalQuestions: number;
}

/** What the Save bar's status line was last told, and which time it was told. */
interface EditSessionStatus {
  id: number;
  text: string;
  tone: 'error' | 'info';
}

interface QuestionCopyTarget {
  questions: EditableQuestion[];
  scope: QuestionSectionKind;
}

function getAnnotationItemLabel(note: string, exact: string, kind: 'highlight' | 'note') {
  const trimmedNote = note.trim();

  if (trimmedNote) {
    return trimmedNote;
  }

  const trimmedQuote = exact.trim();

  if (trimmedQuote) {
    return trimmedQuote;
  }

  return kind === 'note' ? 'Untitled note' : 'Untitled highlight';
}

function buildAnnotationSidebarGroups(
  component: NonNullable<ReturnType<typeof useComponent>['data']>,
  filteredAnnotations: ReturnType<typeof useAnnotations>['filteredAnnotations'],
): AnnotationSidebarGroup[] {
  const textUnits = component.sections.flatMap((section) =>
    section.units.filter((unit) => unit.kind === 'text'),
  );
  const unitMap = new Map(textUnits.map((unit) => [unit.id, unit]));
  const groupedItems = new Map<string, AnnotationSidebarGroup['items']>();

  filteredAnnotations.forEach(({ annotation, contentUnitId }) => {
    const unit = unitMap.get(contentUnitId);
    const label =
      unit?.numberedHeading ?? unit?.heading ?? unit?.summaryLabel ?? 'Unmatched text';
    const plainText = unit ? extractPlainText(unit.html).text : '';
    const resolution = unit
      ? resolveTextQuote(plainText, annotation.quote)
      : {
          confidence: 'none',
          status: 'orphaned',
          strategy: 'orphaned',
        };
    const groupKey = unit?.id ?? 'orphaned-annotations';
    const nextItems = groupedItems.get(groupKey) ?? [];

    nextItems.push({
      annotation,
      isOrphaned: resolution.status === 'orphaned',
      label: getAnnotationItemLabel(
        annotation.note,
        annotation.quote.exact,
        annotation.kind,
      ),
    });
    groupedItems.set(groupKey, nextItems);
  });

  const groups = textUnits
    .map((unit) => ({
      id: unit.id,
      items: groupedItems.get(unit.id) ?? [],
      label: unit.numberedHeading ?? unit.heading ?? unit.summaryLabel,
    }))
    .filter((group) => group.items.length > 0);
  const orphanedItems = groupedItems.get('orphaned-annotations') ?? [];

  if (orphanedItems.length > 0) {
    groups.push({
      id: 'orphaned-annotations',
      items: orphanedItems,
      label: 'Unmatched text',
    });
  }

  return groups;
}

export function ContentPage() {
  const { contentId, courseId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const courseQuery = useCourse(courseId);
  const userRole = useUserRole(courseQuery.data);
  const isInstructionalView = isInstructionalRole(userRole);
  const annotationToggleRef = useRef<HTMLButtonElement | null>(null);
  const chatAvatarRef = useRef<HTMLButtonElement | null>(null);
  const chatCloseTimeoutRef = useRef<number | null>(null);
  const assistantMessageCountRef = useRef(0);
  const wasAnnotationsOpenRef = useRef(false);
  const wasChatOpenRef = useRef(false);
  const componentQuery = useComponent(
    courseId,
    contentId,
    courseQuery.data?.isInstructorView === true,
    {
      coursePrelectureQuestionSettings:
        courseQuery.data?.coursePrelectureQuestionSettings,
      courseQuestionSettings: courseQuery.data?.courseQuestionSettings,
    },
  );
  const component = componentQuery.data;
  // Kai Coach modules render their own page (KaiCoachModulePage) and own their
  // own `?edit` toggle, so several content-unit-specific behaviors below are
  // gated on this.
  const coachSection = component ? getKaiCoachSection(component.rawComponent) : null;
  const isCoachComponent = coachSection !== null;
  const initialEditorState = useMemo(
    () => (component?.rawComponent ? createEditorState(component.rawComponent) : null),
    [component?.rawComponent],
  );
  const annotationService = useKuduAnnotationService();
  const chatService = useKuduChatService();
  const studentView = useStudentView();
  const { realTimeline, timeline } = useReaderTimeline(component);
  const reader = { isInstructionalView, timeline };
  // A Kai Coach module hides content in a way the component timeline cannot
  // express: individual sessions open on their own dates. Without this, a
  // released coach with future sessions reported "nothing hidden" and the
  // banner never offered "Show all content".
  const coachHasLockedSessions = useMemo(() => {
    if (!coachSection) {
      return false;
    }
    const now = Date.now();
    return normalizeCoachSection(coachSection).threads.some(
      (thread) => !isThreadOpen(thread, now),
    );
  }, [coachSection]);
  const componentHasHiddenContent = component
    ? timelineHasHiddenContent(realTimeline, {
        hasHomework: component.controlPanel.hasHomeworkQuestionSection,
        hasPreLecture: component.controlPanel.hasPreLectureQuestionSection,
      }) || coachHasLockedSessions
    : false;
  const reportHiddenContent = studentView.reportHiddenContent;
  const reportComponent = studentView.reportComponent;
  const componentTitle = component?.title;

  useEffect(() => {
    if (!studentView.active) {
      return undefined;
    }

    reportHiddenContent(componentHasHiddenContent);
    return () => reportHiddenContent(false);
  }, [studentView.active, componentHasHiddenContent, reportHiddenContent]);

  // Tell the student-view banner which component is open so it can offer a
  // per-component "Reset test data" action (the course home never does).
  useEffect(() => {
    if (!studentView.active || !contentId) {
      return undefined;
    }

    reportComponent({ id: contentId, title: componentTitle });
    return () => reportComponent(null);
  }, [studentView.active, contentId, componentTitle, reportComponent]);
  const preLectureQuestionEditsRestricted =
    arePreLectureEditsRestricted(timeline);
  const homeworkQuestionEditsRestricted = areHomeworkEditsRestricted(timeline);
  const getQuestionSectionRestriction = useCallback(
    (kind: QuestionSectionKind) =>
      kind === 'preLecture'
        ? preLectureQuestionEditsRestricted
        : homeworkQuestionEditsRestricted,
    [homeworkQuestionEditsRestricted, preLectureQuestionEditsRestricted],
  );
  const { isNavOpen, toggleNavigation } = useNavigationPanelState();
  const canEditContent = userRole === 'instructor';
  const contentEditMode = useContentEditModeController({
    canEdit: canEditContent,
    initialEditorState,
  });
  const activeQuestionStructuralEditsRestricted =
    contentEditMode.activeQuestionEditTarget
      ? getQuestionSectionRestriction(contentEditMode.activeQuestionEditTarget.kind)
      : false;
  const { openEditMode } = contentEditMode;
  const saveComponent = useSaveComponent();
  const annotationUserId = getKuduUserId(user) ?? user?.uid ?? null;
  const updateStatsMutation = useUpdateStats();
  const downloadDetailedScoresMutation = useDownloadDetailedScores();
  const [hideStats, setHideStats] = useState(false);
  const [editStatus, setEditStatus] = useState<EditSessionStatus | null>(null);
  // Counted apart from the state: a status cleared and set again in one click
  // would otherwise get the id of the one before it.
  const editStatusCountRef = useRef(0);
  const showEditStatus = useCallback((text: string, tone: 'error' | 'info') => {
    editStatusCountRef.current += 1;
    setEditStatus({ id: editStatusCountRef.current, text, tone });
  }, []);
  // A status describes the chapter as it was when it was said, and the next
  // change makes it stale; the line then says what is true now. "Content
  // saved." used to outrank every status after it, so a further edit left it
  // showing over an enabled Save button. Compared after every render, against
  // the chapter as it stood the first time the status was shown.
  const editStatusRecordRef = useRef<{ chapter: unknown[]; id: number } | null>(
    null,
  );
  const chapterForStatus = [
    contentEditMode.editor.editorState?.baseComponent,
    contentEditMode.editor.editorState?.contentUnits,
    contentEditMode.editor.editorState?.preLectureQuestions,
    contentEditMode.editor.editorState?.questions,
    contentEditMode.editor.editorState?.videoIds,
    contentEditMode.hasLocalChanges,
    contentEditMode.isAnyUnitActive,
  ];

  useEffect(() => {
    const record = editStatusRecordRef.current;

    if (!editStatus) {
      editStatusRecordRef.current = null;
      return;
    }

    if (!record || record.id !== editStatus.id) {
      editStatusRecordRef.current = { chapter: chapterForStatus, id: editStatus.id };
      return;
    }

    if (record.chapter.some((value, index) => value !== chapterForStatus[index])) {
      editStatusRecordRef.current = null;
      setEditStatus(null);
    }
  });
  const [titleError, setTitleError] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const editSessionStatusRef = useRef<HTMLParagraphElement>(null);
  const pageSaveButtonRef = useRef<HTMLButtonElement>(null);
  const chapterLayoutRef = useRef<HTMLDivElement>(null);
  const floatingDockRef = useFloatingDockClearance(
    contentEditMode.editMode ? 'edit' : 'view',
  );
  const [activeStudentPanel, setActiveStudentPanel] = useState<
    'annotations' | 'chat' | null
  >(null);
  const [activeDraggedUnitId, setActiveDraggedUnitId] = useState<string | null>(
    null,
  );
  const [activeDraggedUnitWidth, setActiveDraggedUnitWidth] = useState<
    number | null
  >(null);
  const [copySectionTargets, setCopySectionTargets] = useState<ContentSection[]>(
    [],
  );
  const [copyQuestionTarget, setCopyQuestionTarget] =
    useState<QuestionCopyTarget | null>(null);
  const [sectionSelectionResetKey, setSectionSelectionResetKey] = useState(0);
  const [activeMultiSelectScopes, setActiveMultiSelectScopes] = useState<
    Set<string>
  >(() => new Set());
  const [hasNewAssistantResponse, setHasNewAssistantResponse] = useState(false);
  const [isChatClosing, setIsChatClosing] = useState(false);
  const [isUserTypingInChat, setIsUserTypingInChat] = useState(false);
  const [isPdfExportDialogOpen, setIsPdfExportDialogOpen] = useState(false);
  const [isRegradeDialogOpen, setIsRegradeDialogOpen] = useState(false);
  const showLessonContent = isLessonContentShown(reader);
  const displayedVideoIds =
    contentEditMode.editMode && contentEditMode.editor.editorState
      ? contentEditMode.editor.videoIds
      : component?.videoIds ?? [];
  const showVideoSection =
    (displayedVideoIds.length > 0 || (canEditContent && contentEditMode.editMode)) &&
    showLessonContent;
  const isChapterEmpty =
    !showVideoSection && (component?.questionSections.length ?? 0) === 0;
  const isMultiSelectActive = activeMultiSelectScopes.size > 0;
  const sortableContentUnitIds = useMemo(
    () => contentEditMode.editor.contentUnits.map((unit) => unit.id),
    [contentEditMode.editor.contentUnits],
  );
  const editableUnitIndexById = useMemo(
    () =>
      new Map(
        contentEditMode.editor.contentUnits.map((unit, index) => [
          unit.id,
          index,
        ]),
      ),
    [contentEditMode.editor.contentUnits],
  );
  const sortableSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const renderedContentSections = useMemo(() => {
    if (!component) {
      return [];
    }

    if (!contentEditMode.editMode || contentEditMode.editor.contentUnits.length === 0) {
      return component.sections;
    }

    return createEditModeSections(
      contentEditMode.editor.contentUnits,
      component.sections,
    );
  }, [
    component,
    contentEditMode.editMode,
    contentEditMode.editor.contentUnits,
  ]);
  const navigationTocEntries = useMemo(() => {
    if (!component) {
      return [];
    }

    if (!contentEditMode.editMode || !contentEditMode.editor.editorState) {
      // An entry for something the page does not show goes nowhere: content
      // before its visibility window opens, or a video that is not shown.
      return component.tocEntries.filter((entry) =>
        entry.type === 'content'
          ? showLessonContent
          : entry.type === 'video'
            ? showVideoSection
            : true,
      );
    }

    // The draft's questions, not the saved ones: a question added in the
    // dialog is listed at once, not once the save lands.
    return createEditModeTocEntries(
      contentEditMode.editor.contentUnits,
      displayedVideoIds,
      contentEditMode.editor.questions.length +
        contentEditMode.editor.preLectureQuestions.length,
    );
  }, [
    component,
    contentEditMode.editMode,
    contentEditMode.editor.contentUnits,
    contentEditMode.editor.editorState,
    contentEditMode.editor.preLectureQuestions,
    contentEditMode.editor.questions,
    displayedVideoIds,
    showLessonContent,
    showVideoSection,
  ]);
  const navReorderSections = useMemo(
    () =>
      contentEditMode.editMode
        ? createNavReorderSections(contentEditMode.editor.contentUnits)
        : [],
    [contentEditMode.editMode, contentEditMode.editor.contentUnits],
  );
  const contentActionSections = useMemo(
    () =>
      contentEditMode.editMode
        ? createContentActionSections(contentEditMode.editor.contentUnits)
        : [],
    [contentEditMode.editMode, contentEditMode.editor.contentUnits],
  );
  const activeDraggedUnit = useMemo(() => {
    if (!activeDraggedUnitId) {
      return null;
    }

    for (const section of renderedContentSections) {
      const unit = section.units.find(
        (candidate) => candidate.id === activeDraggedUnitId,
      );

      if (unit) {
        return unit;
      }
    }

    return null;
  }, [activeDraggedUnitId, renderedContentSections]);
  const renderedContentSectionEntries = useMemo(() => {
    const keyOccurrences = new Map<string, number>();

    return renderedContentSections.map((section) => {
      const baseKey = getSectionRenderKey(section);
      const occurrence = keyOccurrences.get(baseKey) ?? 0;

      keyOccurrences.set(baseKey, occurrence + 1);

      return {
        key:
          occurrence === 0
            ? baseKey
            : `${baseKey}:duplicate-${occurrence + 1}`,
        section,
      };
    });
  }, [renderedContentSections]);
  const isQuestionEditMode = canEditContent && contentEditMode.editMode;
  const hasEditablePreLectureQuestions =
    contentEditMode.editor.preLectureQuestions.length > 0;
  const emptyHomeworkEditorQuestionSection = useMemo(
    () => createEmptyEditorQuestionSection('homework'),
    [],
  );
  const visibleQuestionSections =
    component?.questionSections.filter((section) => {
      if (section.kind === 'preLecture') {
        const hasPreLectureQuestions = isQuestionEditMode
          ? hasEditablePreLectureQuestions
          : section.questionCount > 0;

        if (!hasPreLectureQuestions) {
          return false;
        }
      }

      return isQuestionSectionShown(section.kind, reader);
    }) ?? [];
  const renderedQuestionSections = useMemo(() => {
    if (!isQuestionEditMode) {
      return visibleQuestionSections;
    }

    if (visibleQuestionSections.some((section) => section.kind === 'homework')) {
      return visibleQuestionSections;
    }

    return [...visibleQuestionSections, emptyHomeworkEditorQuestionSection];
  }, [
    emptyHomeworkEditorQuestionSection,
    isQuestionEditMode,
    visibleQuestionSections,
  ]);
  // The chapter can be on screen before the course has been read. With nothing
  // of its own to go on it would read as "at the due date", so a course set to
  // 1 week or Never would show its solutions until the read came back (issue
  // #144).
  const isCourseRead = courseQuery.data !== undefined;
  const getQuestionSectionState = (kind: 'homework' | 'preLecture') =>
    kind === 'preLecture'
      ? {
          areSolutionsVisible: isCourseRead && timeline.arePreLectureSolutionsVisible,
          isClosed: timeline.isPreLectureClosed,
          isOpen: timeline.isPreLectureOpen,
        }
      : {
          areSolutionsVisible: isCourseRead && timeline.areHomeworkSolutionsVisible,
          isClosed: timeline.isHomeworkClosed,
          isOpen: timeline.isHomeworkOpen,
        };

  useEffect(() => {
    setHideStats(false);
  }, [contentId]);

  useEffect(() => {
    // Kai Coach modules manage `?edit` themselves (KaiCoachModulePage); consuming
    // it here would strip the param and bounce the coach out of edit mode.
    if (isCoachComponent) {
      return;
    }

    const params = new URLSearchParams(location.search);

    if (params.get('edit') !== '1') {
      return;
    }

    if (canEditContent) {
      openEditMode();
    }

    params.delete('edit');
    const nextSearch = params.toString();

    navigate(
      {
        hash: location.hash,
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true },
    );
  }, [
    canEditContent,
    isCoachComponent,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    openEditMode,
  ]);

  const isChatAvailable = useChatAvailability({
    component,
    course: courseQuery.data,
    role: userRole,
  });
  // The renderer's image allowlist: exactly the URLs the backend showed the
  // model and verified assistant images against.
  const chapterImageUrls = useMemo(
    () => getChapterImageUrls(component?.rawComponent),
    [component?.rawComponent],
  );
  const chatSession = useChatSession({
    chatService,
    componentId: contentId ?? '',
    courseId: courseId ?? '',
    isAvailable: isChatAvailable,
    // The conversation is keyed by (course, component, user) server-side, so the
    // signed-in student's id is part of its identity.
    userId: getKuduUserId(user) ?? user?.uid ?? null,
  });
  const isAnnotationAvailable = useAnnotationAvailability(component, userRole);
  const annotationSession = useAnnotations({
    componentId: contentId ?? '',
    courseId: courseId ?? '',
    initialAnnotations: component?.contentUnitAnnotations ?? [],
    service: annotationService,
    userId: annotationUserId,
  });
  const annotationGroups = component
    ? buildAnnotationSidebarGroups(component, annotationSession.filteredAnnotations)
    : [];
  const isChatOpen = activeStudentPanel === 'chat';
  const shouldRenderChatPanel = isChatOpen || isChatClosing;
  const isAnnotationsOpen = activeStudentPanel === 'annotations';
  const avatarState = useAvatarState({
    hasError: Boolean(chatSession.error),
    hasNewResponse: hasNewAssistantResponse,
    isAssistantThinking: isChatOpen && chatSession.isLoading,
    isChatOpen,
    isUserTyping: isChatOpen && isUserTypingInChat,
  });

  useEffect(() => {
    if (!isChatAvailable) {
      setActiveStudentPanel((current) => (current === 'chat' ? null : current));
      setIsChatClosing(false);
    }
  }, [isChatAvailable]);

  useEffect(() => {
    return () => {
      if (chatCloseTimeoutRef.current !== null) {
        window.clearTimeout(chatCloseTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const assistantMessageCount = chatSession.messages.filter(
      (message) => message.role === 'assistant' && message.status === 'sent',
    ).length;

    if (isChatOpen && assistantMessageCount > assistantMessageCountRef.current) {
      setHasNewAssistantResponse(true);
      const timeout = window.setTimeout(() => {
        setHasNewAssistantResponse(false);
      }, 3_200);

      assistantMessageCountRef.current = assistantMessageCount;
      return () => window.clearTimeout(timeout);
    }

    assistantMessageCountRef.current = assistantMessageCount;
  }, [chatSession.messages, isChatOpen]);

  useEffect(() => {
    if (!isAnnotationAvailable) {
      setActiveStudentPanel((current) =>
        current === 'annotations' ? null : current,
      );
    }
  }, [isAnnotationAvailable]);

  useEffect(() => {
    if (!annotationSession.showAnnotations) {
      annotationSession.setActiveAnnotation(null);
    }
  }, [annotationSession.setActiveAnnotation, annotationSession.showAnnotations]);

  useEffect(() => {
    if (wasChatOpenRef.current && !isChatOpen) {
      chatAvatarRef.current?.focus();
    }

    wasChatOpenRef.current = isChatOpen;
  }, [isChatOpen]);

  useEffect(() => {
    if (wasAnnotationsOpenRef.current && !isAnnotationsOpen) {
      annotationToggleRef.current?.focus();
    }

    wasAnnotationsOpenRef.current = isAnnotationsOpen;
  }, [isAnnotationsOpen]);

  let announcement = 'Content is ready to read.';

  if (!courseId || !contentId) {
    announcement = 'Unable to determine which content should be loaded.';
  } else if (componentQuery.isPending) {
    announcement = 'Loading content page.';
  } else if (componentQuery.isError && componentQuery.error.kind === 'auth') {
    announcement = 'Your session expired. Redirecting to sign in.';
  } else if (componentQuery.isError) {
    announcement = `Unable to load this content. ${getErrorMessage(componentQuery.error)}`;
  } else if (
    component &&
    showLessonContent &&
    renderedContentSections.length === 0 &&
    (contentEditMode.editMode || isChapterEmpty)
  ) {
    // Said whenever the "No content available yet" card shows, and only then.
    announcement = 'No content sections are available yet.';
  }

  const showStudentReviewSummary =
    userRole === 'student' &&
    component?.timingState.isPastDue === true &&
    component.questionCount > 0 &&
    !canEditContent &&
    !hideStats;
  const studentCanToggleHiddenSolutions =
    userRole === 'student' &&
    visibleQuestionSections.some(
      (section) => getQuestionSectionState(section.kind).areSolutionsVisible,
    );
  const showStatsToolbar =
    (component?.questionSections.length ?? 0) > 0 &&
    (isInstructionalView || studentCanToggleHiddenSolutions);
  const showInstructorControlPanel = canEditContent && Boolean(component);
  const assignedHomeworkQuestions = component
    ? component.questionSections.flatMap((section) =>
        section.kind === 'homework'
          ? section.questions.filter((question) => question.isAssigned)
          : [],
      )
    : [];
  const showInstructorClassSummary =
    isInstructionalView &&
    !hideStats &&
    component?.hasInstructorAggregateStats === true &&
    assignedHomeworkQuestions.length > 0;
  const showStandaloneHeader =
    componentQuery.isPending ||
    componentQuery.isError ||
    !component ||
    !courseId ||
    !contentId;

  function handleAnnotationNavigation(reference: AnnotationReference) {
    const marker = document.getElementById(getAnnotationHighlightId(reference));

    if (marker instanceof HTMLElement) {
      marker.scrollIntoView({ behavior: 'smooth', block: 'center' });
      annotationSession.setActiveAnnotation(reference);
      return;
    }

    const container = document.getElementById(
      getAnnotationContainerId(reference.contentUnitId),
    );

    if (container instanceof HTMLElement) {
      container.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function clearChatCloseTimer() {
    if (chatCloseTimeoutRef.current !== null) {
      window.clearTimeout(chatCloseTimeoutRef.current);
      chatCloseTimeoutRef.current = null;
    }
  }

  function openChatPanel() {
    clearChatCloseTimer();
    setIsChatClosing(false);
    setActiveStudentPanel('chat');
  }

  function closeChatPanel() {
    if (!isChatOpen) {
      return;
    }

    clearChatCloseTimer();
    setIsUserTypingInChat(false);
    setIsChatClosing(true);
    chatCloseTimeoutRef.current = window.setTimeout(() => {
      setActiveStudentPanel((current) => (current === 'chat' ? null : current));
      setIsChatClosing(false);
      chatCloseTimeoutRef.current = null;
    }, 200);
  }

  function toggleChatPanel() {
    if (isChatOpen) {
      closeChatPanel();
      return;
    }

    openChatPanel();
  }

  function openAnnotationsPanel() {
    clearChatCloseTimer();
    setIsChatClosing(false);
    setActiveStudentPanel((current) =>
      current === 'annotations' ? null : 'annotations',
    );
  }

  async function handleNoteTextColorChange(color: AnnotationTextColor) {
    annotationSession.setNoteTextColor(color);

    const activeAnnotation = annotationSession.activeAnnotation;

    if (!activeAnnotation) {
      return;
    }

    const annotation = annotationSession
      .getAnnotationsForContentUnit(activeAnnotation.contentUnitId)
      .find((candidate) => candidate.id === activeAnnotation.annotationId);

    if (!annotation || annotation.kind !== 'note') {
      return;
    }

    await annotationSession.updateAnnotation({
      annotation: {
        ...annotation,
        textColor: color,
      },
      contentUnitId: activeAnnotation.contentUnitId,
    });
  }

  const insertAtDivider = useCallback(
    (index: number) => (type: AddableContentType) => {
      contentEditMode.insertUnitAtDivider(index, type);
    },
    [contentEditMode.insertUnitAtDivider],
  );
  const appendContentUnit = useCallback(
    (type: AddableContentType) => {
      contentEditMode.insertUnitAtDivider(
        contentEditMode.editor.contentUnits.length,
        type,
      );
    },
    [
      contentEditMode.editor.contentUnits.length,
      contentEditMode.insertUnitAtDivider,
    ],
  );

  const handleMultiSelectActiveChange = useCallback(
    (scope: string, active: boolean) => {
      setActiveMultiSelectScopes((currentScopes) => {
        const hasScope = currentScopes.has(scope);

        if (active === hasScope) {
          return currentScopes;
        }

        const nextScopes = new Set(currentScopes);

        if (active) {
          nextScopes.add(scope);
        } else {
          nextScopes.delete(scope);
        }

        return nextScopes;
      });
    },
    [],
  );

  const clearMultiSelect = useCallback(() => {
    setActiveMultiSelectScopes(new Set());
    setSectionSelectionResetKey((key) => key + 1);
  }, []);

  const {
    activeEditUnitId,
    activeQuestionEditTarget,
    autoSaveAndOpenUnit,
    commitActiveUnit,
    deleteUnit: deleteContentUnit,
    editor: editModeEditor,
    hasUnsavedChanges: hasEditModeUnsavedChanges,
    moveUnit: moveContentUnit,
    saveAllChanges: saveAllEditModeChanges,
  } = contentEditMode;
  const activeEditUnitIdRef = useRef(activeEditUnitId);
  const activeQuestionEditTargetRef = useRef(activeQuestionEditTarget);
  const componentQueryRef = useRef(componentQuery);
  const contentIdRef = useRef(contentId);
  const courseIdRef = useRef(courseId);
  const editModeContentUnitsRef = useRef(editModeEditor.contentUnits);
  const editModeEditorRef = useRef(editModeEditor);
  const hasEditModeUnsavedChangesRef = useRef(hasEditModeUnsavedChanges);
  const saveAllEditModeChangesRef = useRef(saveAllEditModeChanges);

  useEffect(() => {
    activeEditUnitIdRef.current = activeEditUnitId;
    activeQuestionEditTargetRef.current = activeQuestionEditTarget;
    componentQueryRef.current = componentQuery;
    contentIdRef.current = contentId;
    courseIdRef.current = courseId;
    editModeContentUnitsRef.current = editModeEditor.contentUnits;
    editModeEditorRef.current = editModeEditor;
    hasEditModeUnsavedChangesRef.current = hasEditModeUnsavedChanges;
    saveAllEditModeChangesRef.current = saveAllEditModeChanges;
  }, [
    activeEditUnitId,
    activeQuestionEditTarget,
    componentQuery,
    contentId,
    courseId,
    editModeEditor,
    editModeEditor.contentUnits,
    hasEditModeUnsavedChanges,
    saveAllEditModeChanges,
  ]);

  // The question last opened in its editor, kept once the editor closes: its
  // Edit button is drawn again then, and takes focus back (#173).
  const lastEditedQuestionTargetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (activeQuestionEditTarget) {
      lastEditedQuestionTargetIdRef.current = activeEditUnitId;
    }
  }, [activeEditUnitId, activeQuestionEditTarget]);

  const findLastEditedQuestionButton = useCallback(() => {
    const targetId = lastEditedQuestionTargetIdRef.current;

    return targetId ? findQuestionEditButton(targetId) : null;
  }, []);

  const finishActiveEditBeforeBulkAction = useCallback(() => {
    if (!activeEditUnitIdRef.current) {
      return true;
    }

    if (activeQuestionEditTargetRef.current) {
      return false;
    }

    return commitActiveUnit();
  }, [commitActiveUnit]);

  const getSingleUnitCopySection = useCallback(
    (unitId: string): ContentSection[] => {
      const units = editModeContentUnitsRef.current;
      const unitIndex = units.findIndex((unit) => unit.id === unitId);
      const unit = units[unitIndex];

      return unit ? [createSingleUnitCopySection(unit, unitIndex)] : [];
    },
    [],
  );

  const openContentCopyDialog = useCallback((sections: ContentSection[]) => {
    if (sections.length === 0) {
      return;
    }

    if (!finishActiveEditBeforeBulkAction()) {
      return;
    }

    setCopySectionTargets(sections);
    setSectionSelectionResetKey((key) => key + 1);
  }, [finishActiveEditBeforeBulkAction]);

  const handleFrameCopyUnit = useCallback(
    (unitId: string) => {
      openContentCopyDialog(getSingleUnitCopySection(unitId));
    },
    [getSingleUnitCopySection, openContentCopyDialog],
  );

  const handleFrameDeleteUnit = useCallback(
    (unitId: string) => {
      deleteContentUnit(unitId);
    },
    [deleteContentUnit],
  );

  const handleFrameEditUnit = useCallback(
    (unitId: string) => {
      clearMultiSelect();
      autoSaveAndOpenUnit(unitId);
    },
    [autoSaveAndOpenUnit, clearMultiSelect],
  );

  const handleFrameMoveUnit = useCallback(
    (fromIndex: number, toIndex: number) => {
      moveContentUnit(fromIndex, toIndex);
    },
    [moveContentUnit],
  );

  const confirmAndDeleteContentSections = useCallback(
    (sections: ContentSection[]) => {
      if (sections.length === 0) {
        return;
      }

      if (!finishActiveEditBeforeBulkAction()) {
        return;
      }

      const message =
        sections.length === 1
          ? 'Delete this item? You can undo by canceling page edits before saving.'
          : `Delete ${sections.length} items? You can undo by canceling page edits before saving.`;

      if (!window.confirm(message)) {
        return;
      }

      const unitIds = new Set(
        sections.flatMap((section) =>
          getSectionUnits(section).map((unit) => unit.id),
        ),
      );

      unitIds.forEach((unitId) => {
        deleteContentUnit(unitId);
      });
      setSectionSelectionResetKey((key) => key + 1);
    },
    [deleteContentUnit, finishActiveEditBeforeBulkAction],
  );

  const handleContentCopySuccess = useCallback(
    async (result: CopySectionSuccess) => {
      showEditStatus(result.message, 'info');

      const isCurrentTarget =
        result.targetCourseId === courseIdRef.current &&
        result.targetComponentId === contentIdRef.current;

      if (!isCurrentTarget) {
        return;
      }

      const nextState = createEditorState(result.savedComponent);

      if (!hasEditModeUnsavedChangesRef.current) {
        editModeEditorRef.current.replaceState(nextState);
        await saveAllEditModeChangesRef.current(nextState);
        return;
      }

      // Not reached for a copy made from this page: the copy dialogs ask for
      // unsaved changes to be saved or cancelled before copying into it.
      void componentQueryRef.current.refetch();
    },
    [showEditStatus],
  );

  const handleQuestionCopySuccess = useCallback(
    async (result: CopyQuestionSuccess) => {
      showEditStatus(result.message, 'info');

      const isCurrentTarget =
        result.targetCourseId === courseIdRef.current &&
        result.targetComponentId === contentIdRef.current;

      if (!isCurrentTarget) {
        return;
      }

      const nextState = createEditorState(result.savedComponent);

      if (!hasEditModeUnsavedChangesRef.current) {
        editModeEditorRef.current.replaceState(nextState);
        await saveAllEditModeChangesRef.current(nextState);
        return;
      }

      // Not reached for a copy made from this page: the copy dialogs ask for
      // unsaved changes to be saved or cancelled before copying into it.
      void componentQueryRef.current.refetch();
    },
    [showEditStatus],
  );

  function handleContentDragEnd(event: DragEndEvent) {
    setActiveDraggedUnitId(null);
    setActiveDraggedUnitWidth(null);

    const { active, over } = event;

    if (
      !over ||
      active.id === over.id ||
      contentEditMode.isAnyUnitActive ||
      isMultiSelectActive
    ) {
      return;
    }

    const fromIndex = editableUnitIndexById.get(String(active.id)) ?? -1;
    const toIndex = editableUnitIndexById.get(String(over.id)) ?? -1;

    if (fromIndex < 0 || toIndex < 0) {
      return;
    }

    contentEditMode.moveUnit(fromIndex, toIndex);
  }

  function handleContentDragCancel() {
    setActiveDraggedUnitId(null);
    setActiveDraggedUnitWidth(null);
  }

  function renderInsertDivider(index: number, key: string) {
    if (!contentEditMode.editMode) {
      return null;
    }

    return <DividerInsertButton key={key} onAdd={insertAtDivider(index)} />;
  }

  function renderAddContentButton() {
    if (!contentEditMode.editMode) {
      return null;
    }

    return (
      <AddContentPicker
        ariaLabel="Add Content"
        className={styles.addContentPicker}
        disabled={Boolean(activeQuestionEditTarget) || isMultiSelectActive}
        label="Add Content"
        onAdd={appendContentUnit}
      />
    );
  }

  let visibleEditableUnitIndex = 0;

  function renderUnitWithAnnotations(
    unit: NormalizedContentUnit,
    options?: {
      disableCollapseWrapper?: boolean;
      hideHeading?: boolean;
    },
  ) {
    const unitRenderer = (
      <ContentUnit
        disableCollapseWrapper={options?.disableCollapseWrapper}
        hideHeading={options?.hideHeading}
        key={unit.id}
        unit={unit}
      />
    );

    const renderedUnit =
      !isAnnotationAvailable || unit.kind !== 'text' ? (
        unitRenderer
      ) : (
        <AnnotationLayer
          activeAnnotation={annotationSession.activeAnnotation}
          activeSelection={annotationSession.activeSelection}
          annotations={annotationSession.getAnnotationsForContentUnit(unit.id)}
          highlightColor={annotationSession.highlightColor}
          noteTextColor={annotationSession.noteTextColor}
          onCreateAnnotation={annotationSession.createAnnotation}
          onDeleteAnnotation={annotationSession.deleteAnnotation}
          onSelectionChange={annotationSession.setActiveSelection}
          onSetActiveAnnotation={annotationSession.setActiveAnnotation}
          onUpdateAnnotation={annotationSession.updateAnnotation}
          showAnnotations={annotationSession.showAnnotations}
          unit={unit}
        >
          {unitRenderer}
        </AnnotationLayer>
      );

    const editableUnitIndex = editableUnitIndexById.get(unit.id) ?? -1;
    const visibleUnitIndex = visibleEditableUnitIndex;
    visibleEditableUnitIndex += 1;
    const insertionIndex =
      editableUnitIndex >= 0 ? editableUnitIndex : visibleUnitIndex;
    const editableUnit =
      editableUnitIndex >= 0
        ? contentEditMode.editor.contentUnits[editableUnitIndex]
        : undefined;
    const isActive = contentEditMode.activeEditUnitId === unit.id;
    const hasActiveEditor = isActive && Boolean(editableUnit);
    return (
      <EditableUnitFrame
        activeEditor={
          hasActiveEditor && editableUnit ? (
            <ContentUnitEditMode
              index={insertionIndex}
              onCancel={contentEditMode.cancelActiveUnit}
              onDirtyChange={() => {
                // Draft syncing is handled by onDraftChange; this preserves the
                // existing editor callback contract without marking a backend save.
              }}
              onDraftChange={(draftUnit) => {
                contentEditMode.setUnitDraft(unit.id, draftUnit);
              }}
              onSave={(draftUnit) => {
                contentEditMode.commitActiveUnit(draftUnit);
              }}
              unit={editableUnit}
            />
          ) : undefined
        }
        disabledReason={
          isMultiSelectActive
            ? 'Exit multi-select to reorder'
            : contentEditMode.isAnyUnitActive
              ? 'Save or cancel current edit first'
              : undefined
        }
        editMode={contentEditMode.editMode}
        anchorId={getContentUnitAnchorId(unit.id)}
        isActive={hasActiveEditor}
        isDragDisabled={
          contentEditMode.isAnyUnitActive || isMultiSelectActive
        }
        key={unit.id}
        canMoveDown={
          editableUnitIndex >= 0 &&
          editableUnitIndex < contentEditMode.editor.contentUnits.length - 1
        }
        canMoveUp={editableUnitIndex > 0}
        onDeleteUnit={handleFrameDeleteUnit}
        onCopyUnit={handleFrameCopyUnit}
        onEditUnit={handleFrameEditUnit}
        onMoveUnit={handleFrameMoveUnit}
        moveIndex={editableUnitIndex}
        unitIndex={visibleUnitIndex}
        unitId={unit.id}
        isTinted={unit.collapseByDefault}
      >
        {renderedUnit}
      </EditableUnitFrame>
    );
  }

  function renderDragOverlayUnit(unit: NormalizedContentUnit) {
    return (
      <DragPreview unit={unit} width={activeDraggedUnitWidth} />
    );
  }

  function renderHomeworkDueCard() {
    if (!component) {
      return null;
    }

    const assignmentBlocks = component.questionSections
      .map((section): AssignmentDueBlock | null => {
        const dueDate = getAssignmentDueDate(section.kind, timeline);

        if (!dueDate) {
          return null;
        }

        const assignedQuestions = section.questions.filter(
          (question) => question.isAssigned,
        );
        const hasQuestionDetails = section.questions.length > 0;
        const totalQuestions = hasQuestionDetails
          ? assignedQuestions.length
          : section.questionCount;

        if (totalQuestions === 0) {
          return null;
        }

        const completedQuestions = hasQuestionDetails
          ? assignedQuestions.filter(isQuestionCompleted).length
          : 0;
        const label = getAssignmentDisplayName(section.kind);
        // A chapter's pre-lecture and homework sets can share their section's id.
        const renderedIndex = renderedQuestionSections.findIndex(
          (candidate) =>
            candidate.id === section.id && candidate.kind === section.kind,
        );

        return {
          anchorId:
            renderedIndex >= 0 ? getQuestionSectionAnchorId(renderedIndex) : null,
          completedQuestions,
          dueDate,
          id: section.id,
          isClosed: getQuestionSectionState(section.kind).isClosed,
          isComplete:
            !isInstructionalView &&
            totalQuestions > 0 &&
            completedQuestions >= totalQuestions,
          kind: section.kind,
          label,
          totalQuestions,
        };
      })
      .filter((block): block is AssignmentDueBlock => block !== null)
      .sort(sortAssignmentBlocks);

    if (assignmentBlocks.length === 0) {
      return null;
    }

    return (
      <aside
        aria-label="Assignments due"
        className={styles.homeworkDueCard}
      >
        {assignmentBlocks.map((block) => {
          const progressPercentage =
            block.totalQuestions > 0
              ? Math.round(
                  (block.completedQuestions / block.totalQuestions) * 100,
                )
              : 0;
          // Saved answers count: they are graded at the close. The card calls
          // them answered, never completed, so it does not promise a score.
          const eyebrowLabel = `${block.label.toUpperCase()} · ${
            block.isClosed ? 'CLOSED' : block.isComplete ? 'ALL ANSWERED' : 'DUE'
          }`;
          const ctaLabel = `Continue ${block.label.toLowerCase()}`;

          return (
            <section
              aria-label={`${block.label} assignment`}
              className={styles.assignmentBlock}
              data-complete={block.isComplete && !block.isClosed ? 'true' : 'false'}
              key={`${block.kind}-${block.id}`}
            >
              <div className={styles.homeworkEyebrow}>
                {block.isComplete && !block.isClosed ? (
                  <CheckCircle aria-hidden="true" size={12} />
                ) : (
                  <Calendar aria-hidden="true" size={12} />
                )}
                <span>{eyebrowLabel}</span>
              </div>
              <p className={styles.homeworkDueDate}>
                {formatDueCardDate(block.dueDate)}
              </p>

              {isInstructionalView ? (
                <p className={styles.assignmentSummary}>
                  {block.label}{' '}
                  <span aria-hidden="true">·</span>{' '}
                  {getQuestionCountLabel(block.totalQuestions)}
                </p>
              ) : block.isComplete ? (
                <div className={styles.homeworkComplete}>
                  <CheckCircle aria-hidden="true" size={16} />
                  <span>All questions answered</span>
                </div>
              ) : (
                <div className={styles.homeworkProgress}>
                  <p className={styles.homeworkProgressText}>
                    {block.completedQuestions} of {block.totalQuestions} questions answered
                  </p>
                  <div className={styles.progressRow}>
                    <div
                      aria-label={`${block.label} progress: ${block.completedQuestions} of ${block.totalQuestions} questions`}
                      aria-valuemax={block.totalQuestions}
                      aria-valuemin={0}
                      aria-valuenow={block.completedQuestions}
                      className={styles.progressBar}
                      role="progressbar"
                    >
                      <span
                        className={styles.progressFill}
                        style={{ width: `${progressPercentage}%` }}
                      />
                    </div>
                    <span className={styles.progressPercent}>
                      {progressPercentage}%
                    </span>
                  </div>
                  {/* Nothing to continue once the set has closed. */}
                  {!block.isClosed && block.anchorId ? (
                    <a className={styles.homeworkCta} href={`#${block.anchorId}`}>
                      <span>{ctaLabel}</span>
                      <ArrowRight aria-hidden="true" size={14} />
                    </a>
                  ) : null}
                </div>
              )}
            </section>
          );
        })}
      </aside>
    );
  }

  const isPageSaveDisabled =
    !contentEditMode.hasLocalChanges ||
    contentEditMode.isAnyUnitActive ||
    contentEditMode.editor.isSaving ||
    saveComponent.isPending ||
    !contentEditMode.editor.editorState ||
    !courseId ||
    !contentId;
  const isPageSaving = contentEditMode.editor.isSaving || saveComponent.isPending;
  const pageSaveStatus = editStatus
    ? editStatus.text
    : saveComponent.isError
      ? `Save failed. ${getErrorMessage(saveComponent.error)}`
      : contentEditMode.isAnyUnitActive
        ? 'Finish the open unit edit before saving.'
        : isPageSaving
          ? 'Saving changes...'
          : contentEditMode.hasLocalChanges
            ? 'Unsaved changes'
            : 'All changes saved';
  // The page-level dismiss button discards pending edits when there are any, but
  // simply leaves the (already-saved) editing view otherwise. Reflect that in the
  // label so a saved session reads "Exit" instead of an alarming "Cancel".
  const hasPendingEdits =
    contentEditMode.hasLocalChanges || contentEditMode.isAnyUnitActive;
  const pageSaveStatusTone = saveComponent.isError
    ? 'error'
    : editStatus?.tone === 'error'
      ? 'error'
      : contentEditMode.hasLocalChanges || contentEditMode.isAnyUnitActive
        ? 'dirty'
        : 'saved';

  const handlePageLevelCancel = () => {
    saveComponent.reset();
    setEditStatus(null);
    contentEditMode.discardAllChanges();
  };

  const handlePageLevelSave = () => {
    setEditStatus(null);

    if (
      contentEditMode.isAnyUnitActive ||
      !contentEditMode.editor.editorState ||
      !courseId ||
      !contentId
    ) {
      showEditStatus('Finish the open unit edit before saving.', 'error');
      return;
    }

    saveComponent.reset();

    const trimmedTitle = (
      contentEditMode.editor.editorState?.baseComponent?.title ?? ''
    ).trim();

    if (trimmedTitle.length === 0) {
      setTitleError(true);
      showEditStatus('Add a chapter title before saving.', 'error');
      titleInputRef.current?.focus();
      return;
    }

    if (!contentEditMode.editor.validateBeforeSave()) {
      showEditStatus('Fix the highlighted validation errors before saving.', 'error');
      return;
    }

    const editorStateToSave = contentEditMode.editor.editorState;

    if (!editorStateToSave) {
      showEditStatus('Finish the open unit edit before saving.', 'error');
      return;
    }

    contentEditMode.editor.setSaving(true);

    // While the save is on its way the chapter is inert and Save is disabled,
    // and either would drop focus to the page: it waits in the status line.
    const focused = document.activeElement;

    if (
      focused instanceof HTMLElement &&
      (focused === pageSaveButtonRef.current ||
        chapterLayoutRef.current?.contains(focused))
    ) {
      editSessionStatusRef.current?.focus();
    }

    scheduleIdleWork(() => {
      void (async () => {
        await waitForNextFrame();

        try {
          const savedComponent = await saveComponent.mutateAsync({
            componentId: contentId,
            courseId,
            editorState: editorStateToSave,
          });
          const nextState = createEditorState(savedComponent);

          contentEditMode.editor.replaceState(nextState);
          await contentEditMode.saveAllChanges(nextState);
          showEditStatus('Content saved.', 'info');
        } catch (error) {
          contentEditMode.editor.setSaving(false);
          showEditStatus(`Save failed. ${getErrorMessage(error as ApiError)}`, 'error');
        }
      })();
    });
  };

  const homeworkDueCard = renderHomeworkDueCard();
  const hasNotesLane = isAnnotationAvailable || Boolean(homeworkDueCard);
  const printableChapter = component ? getPrintableChapter(component, reader) : null;
  const showEditContentFab =
    canEditContent && courseId && contentId && !contentEditMode.editMode;
  const hasChapterActions = Boolean(component && courseId && contentId);
  const hasFloatingActions =
    !shouldRenderChatPanel &&
    (Boolean(showEditContentFab) || hasChapterActions || isChatAvailable);
  // The page-level Save and Cancel bar. It shares the bottom-right corner with
  // the floating actions, so the two render inside one dock that stacks them.
  const showEditSessionBar =
    contentEditMode.editMode &&
    !contentEditMode.activeQuestionEditTarget &&
    !isMultiSelectActive;

  // Kai Coach modules render their own module page (with its own navigation,
  // instructor edit toggle, and visibility controls) instead of the standard
  // content/question page. All hooks above have already run, so this early
  // return is safe. (`coachSection`/`isCoachComponent` are computed up top.)
  if (coachSection && component && courseId && contentId) {
    return (
      <KaiCoachModulePage
        canEdit={canEditContent}
        component={component}
        courseId={courseId}
        isVisibleToStudents={timeline.isContentVisible}
        moduleId={contentId}
        section={coachSection}
      />
    );
  }

  // Both scopes resolved here because this is the only boundary that has the
  // course. The question boundary downstream switches between them without
  // needing to look anything up.
  const imageSizeOverride = {
    active: resolveImageSizeOverrideForSurface(
      courseQuery.data?.courseSettings,
      'chapter',
    ),
    question: resolveImageSizeOverrideForSurface(
      courseQuery.data?.courseSettings,
      'question',
    ),
  };

  return (
    <ImageSizeOverrideProvider value={imageSizeOverride}>
    <ContentEditModeProvider value={contentEditMode}>
      <ContentEditNavigationGuard
        onDiscard={contentEditMode.discardAllChanges}
        shouldBlock={contentEditMode.hasUnsavedChanges}
      />
      <section aria-labelledby="content-page-heading" className={styles.page}>
      {showStandaloneHeader ? (
        <div className={styles.header}>
          <div className={styles.intro}>
            <h1 className={styles.title} id="content-page-heading">
              {component?.title ?? 'Content'}
            </h1>
          </div>

          {homeworkDueCard ? (
            <div className={styles.standaloneSidebar}>{homeworkDueCard}</div>
          ) : null}
        </div>
      ) : null}

      <p aria-atomic="true" aria-live="polite" className={styles.liveRegion}>
        {announcement}
      </p>

      {!courseId || !contentId ? (
        <Card className={styles.stateCard} role="alert">
          <h2 className={styles.stateTitle}>Missing content link</h2>
          <p className={styles.stateMessage}>
            This link is incomplete, so the content page could not be loaded.
          </p>
        </Card>
      ) : null}

      {componentQuery.isPending ? (
        <Card className={styles.stateCard} role="status">
          <h2 className={styles.stateTitle}>Loading content</h2>
          <p className={styles.stateMessage}>
            Gathering the lesson content and preparing the section view.
          </p>
        </Card>
      ) : null}

      {componentQuery.isError && componentQuery.error.kind === 'auth' ? (
        <Card className={styles.stateCard} role="status">
          <h2 className={styles.stateTitle}>Redirecting to sign in</h2>
          <p className={styles.stateMessage}>
            Your session needs to be refreshed before this content can be shown.
          </p>
        </Card>
      ) : null}

      {componentQuery.isError && componentQuery.error.kind !== 'auth' ? (
        <Card className={styles.stateCard} role="alert">
          <h2 className={styles.stateTitle}>Unable to load this content</h2>
          <p className={styles.stateMessage}>{getErrorMessage(componentQuery.error)}</p>
          <Button
            onClick={() => {
              void componentQuery.refetch();
            }}
            variant="primary"
          >
            Try again
          </Button>
        </Card>
      ) : null}

      {!componentQuery.isPending && !componentQuery.isError && component && courseId && contentId ? (
        <>
          <div
            className={styles.layout}
            data-has-sidebar={hasNotesLane ? 'true' : 'false'}
            data-nav-open={isNavOpen ? 'true' : 'false'}
            // An edit made while a save is on its way would be written over
            // by the server's copy when it answers.
            inert={isPageSaving}
            ref={chapterLayoutRef}
          >
            <NavigationPanel
              courseId={courseId}
              courseTitle={component.title}
              isOpen={isNavOpen}
              navReorder={
                canEditContent
                  ? {
                      disabledReason: isMultiSelectActive
                        ? 'Exit multi-select to reorder'
                        : 'Save or cancel current edit first',
                      isDragDisabled:
                        contentEditMode.isAnyUnitActive || isMultiSelectActive,
                      isEnabled: contentEditMode.editMode,
                      onReorderEntry: contentEditMode.moveNavEntry,
                      sections: navReorderSections,
                    }
                  : undefined
              }
              onSelectionActiveChange={(active) => {
                handleMultiSelectActiveChange('content-nav', active);
              }}
              onToggle={toggleNavigation}
              sectionActions={
                canEditContent && contentEditMode.editMode
                  ? {
                      actionDisabledReason: 'Save or cancel current edit first',
                      isActionDisabled: Boolean(
                        contentEditMode.activeQuestionEditTarget,
                      ),
                      isDragDisabled: contentEditMode.isAnyUnitActive,
                      onBeforeSelectMultiple: finishActiveEditBeforeBulkAction,
                      onCopySections: openContentCopyDialog,
                      onDeleteSections: confirmAndDeleteContentSections,
                      onReorderSection: () => undefined,
                      sections: contentActionSections,
                      selectionResetKey: sectionSelectionResetKey,
                    }
                  : undefined
              }
              tocEntries={navigationTocEntries}
            />

            <div className={styles.workspace}>
              <div className={styles.header}>
                <div className={styles.intro}>
                  {contentEditMode.editMode && canEditContent ? (
                    <>
                      <input
                        aria-describedby={
                          titleError ? 'chapter-title-error' : undefined
                        }
                        aria-invalid={titleError || undefined}
                        aria-label="Chapter title"
                        className={styles.titleInput}
                        onChange={(event) => {
                          if (titleError && event.target.value.trim()) {
                            setTitleError(false);
                          }
                          contentEditMode.editor.updateComponentTitle(
                            event.target.value,
                          );
                        }}
                        placeholder="Untitled chapter"
                        ref={titleInputRef}
                        type="text"
                        value={
                          contentEditMode.editor.editorState?.baseComponent
                            ?.title ?? ''
                        }
                      />
                      {titleError ? (
                        <p
                          className={styles.titleError}
                          id="chapter-title-error"
                          role="alert"
                        >
                          A chapter title is required.
                        </p>
                      ) : null}
                      {/* Keep an accessible heading so the page section's
                          aria-labelledby and the document outline stay intact
                          while the title is edited. */}
                      <h1 className={styles.srOnly} id="content-page-heading">
                        {contentEditMode.editor.editorState?.baseComponent
                          ?.title || 'Untitled chapter'}
                      </h1>
                    </>
                  ) : (
                    <h1 className={styles.title} id="content-page-heading">
                      {component.title ?? 'Content'}
                    </h1>
                  )}
                  {userRole === 'instructor' &&
                  !timeline.isContentVisible &&
                  timeline.contentVisibleDate ? (
                    <p className={styles.description}>
                      Scheduled for {formatScheduledDate(timeline.contentVisibleDate)}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className={styles.contentGrid}>
                <div className={styles.contentColumn}>
                  {showInstructorControlPanel ? (
                    <ControlPanelSummary
                      component={component}
                      componentId={contentId}
                      courseId={courseId}
                      coursePrelectureQuestionSettings={
                        courseQuery.data?.coursePrelectureQuestionSettings
                      }
                      courseQuestionSettings={
                        courseQuery.data?.courseQuestionSettings
                      }
                    />
                  ) : null}

                  {showVideoSection ? (
                    <Card as="section" className={styles.panel} id={VIDEO_SECTION_ID}>
                      <VideoSection
                        canEdit={canEditContent}
                        componentId={contentId}
                        courseId={courseId}
                        onBeforeEdit={clearMultiSelect}
                        rawComponent={component.rawComponent}
                        userRole={userRole}
                        videoIds={displayedVideoIds}
                        videoSectionMap={component.videoSectionMap}
                        videoSectionStats={component.videoSectionStats ?? {}}
                      />
                    </Card>
                  ) : null}

                  {showLessonContent ? (
                    renderedContentSections.length > 0 ? (
                      <DndContext
                        collisionDetection={closestCenter}
                        onDragCancel={handleContentDragCancel}
                        onDragEnd={handleContentDragEnd}
                        onDragStart={({ active }) => {
                          setActiveDraggedUnitId(String(active.id));
                          setActiveDraggedUnitWidth(
                            active.rect.current.initial?.width ?? null,
                          );
                        }}
                        sensors={sortableSensors}
                      >
                        <SortableContext
                          items={sortableContentUnitIds}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className={`${styles.sections} lesson-content-scope`}>
                            {renderedContentSectionEntries.map(({ key, section }, index) => {
                              const renderableUnits = createRenderableUnits(section.units);
                              const sectionStartInsertionIndex = visibleEditableUnitIndex;

                              return (
                                <Card
                                  aria-label={`Content section ${index + 1}`}
                                  as="section"
                                  className={styles.section}
                                  data-reading-card="true"
                                  key={key}
                                  variant="standard"
                                >
                                  <div
                                    className={styles.unitStack}
                                    data-edit-mode={
                                      contentEditMode.editMode ? 'true' : 'false'
                                    }
                                  >
                                    {renderableUnits.length === 0
                                      ? renderAddContentButton()
                                      : renderInsertDivider(
                                          sectionStartInsertionIndex,
                                          `divider-before-${section.units[0]?.id ?? key}`,
                                        )}
                                    {renderableUnits.map((renderableUnit) => {
                                      if (renderableUnit.kind === 'single') {
                                        const editableUnitIndex =
                                          editableUnitIndexById.get(
                                            renderableUnit.unit.id,
                                          ) ?? -1;
                                        const visibleUnitIndex = visibleEditableUnitIndex;
                                        const afterInsertionIndex =
                                          editableUnitIndex >= 0
                                            ? editableUnitIndex + 1
                                            : visibleUnitIndex + 1;
                                        const renderedUnit = renderUnitWithAnnotations(
                                          renderableUnit.unit,
                                        );

                                        return (
                                          <Fragment key={renderableUnit.unit.id}>
                                            {renderedUnit}
                                            {afterInsertionIndex >=
                                            contentEditMode.editor.contentUnits.length
                                              ? renderAddContentButton()
                                              : renderInsertDivider(
                                                  afterInsertionIndex,
                                                  `divider-after-${renderableUnit.unit.id}`,
                                                )}
                                          </Fragment>
                                        );
                                      }

                                      const groupStartVisibleIndex =
                                        visibleEditableUnitIndex;
                                      const firstGroupUnit = renderableUnit.units[0];
                                      const lastGroupUnit =
                                        renderableUnit.units[
                                          renderableUnit.units.length - 1
                                        ];
                                      const firstEditableIndex = firstGroupUnit
                                        ? editableUnitIndexById.get(firstGroupUnit.id) ?? -1
                                        : -1;
                                      const lastEditableIndex = lastGroupUnit
                                        ? editableUnitIndexById.get(lastGroupUnit.id) ?? -1
                                        : -1;
                                      const groupStartInsertionIndex =
                                        firstEditableIndex >= 0
                                          ? firstEditableIndex
                                          : groupStartVisibleIndex;
                                      const groupChildren = renderableUnit.units.map(
                                        (unit, groupedUnitIndex) =>
                                          renderUnitWithAnnotations(unit, {
                                            disableCollapseWrapper: true,
                                            hideHeading:
                                              groupedUnitIndex === 0 &&
                                              Boolean(unit.heading),
                                          }),
                                      );
                                      const groupEndVisibleIndex =
                                        visibleEditableUnitIndex - 1;
                                      const groupEndInsertionIndex =
                                        lastEditableIndex >= 0
                                          ? lastEditableIndex
                                          : groupEndVisibleIndex;
                                      const groupRenderKey =
                                        getCollapsedGroupRenderKey(
                                          renderableUnit.units,
                                        );
                                      const groupDividerKey = lastGroupUnit
                                        ? `divider-after-${lastGroupUnit.id}`
                                        : `divider-after-${groupRenderKey}`;

                                      return (
                                        <Fragment key={groupRenderKey}>
                                          <CollapsibleUnit
                                            editMode={contentEditMode.editMode}
                                            summaryLabel={renderableUnit.summaryLabel}
                                          >
                                            <div
                                              className={styles.unitStack}
                                              data-edit-mode={
                                                contentEditMode.editMode
                                                  ? 'true'
                                                  : 'false'
                                              }
                                            >
                                              {groupChildren}
                                            </div>
                                          </CollapsibleUnit>
                                          {groupEndInsertionIndex + 1 >=
                                          contentEditMode.editor.contentUnits.length
                                            ? renderAddContentButton()
                                            : renderInsertDivider(
                                                groupEndInsertionIndex + 1,
                                                groupDividerKey,
                                              )}
                                        </Fragment>
                                      );
                                    })}
                                  </div>
                                </Card>
                              );
                            })}
                          </div>
                        </SortableContext>
                        <DragOverlay adjustScale={false}>
                          {activeDraggedUnit
                            ? renderDragOverlayUnit(activeDraggedUnit)
                            : null}
                        </DragOverlay>
                      </DndContext>
                    ) : contentEditMode.editMode || isChapterEmpty ? (
                      // A reader is told so only when there is nothing at all:
                      // above a video or questions it read as an error.
                      <Card className={styles.stateCard}>
                        <h2 className={styles.stateTitle}>No content available yet</h2>
                        <p className={styles.stateMessage}>
                          This chapter has no content yet.
                        </p>
                        {renderAddContentButton()}
                      </Card>
                    ) : null
                  ) : (
                    <Card className={styles.stateCard}>
                      <h2 className={styles.stateTitle}>Content not visible yet</h2>
                      <p className={styles.stateMessage}>
                        Lesson content will appear when its visibility window begins.
                      </p>
                    </Card>
                  )}

                  {renderedQuestionSections.length > 0 ? (
                    <>
                      {showStatsToolbar ? (
                        <InstructorStatsToolbar
                          audience={userRole === 'student' ? 'student' : 'staff'}
                          errorMessage={
                            canEditContent && updateStatsMutation.isError
                              ? updateStatsMutation.error.message
                              : canEditContent &&
                                  downloadDetailedScoresMutation.isError
                                ? downloadDetailedScoresMutation.error.message
                                : undefined
                          }
                          hideStats={hideStats}
                          isDownloading={
                            canEditContent
                              ? downloadDetailedScoresMutation.isPending
                              : false
                          }
                          isUpdating={
                            canEditContent ? updateStatsMutation.isPending : false
                          }
                          onDownloadDetailedScores={
                            canEditContent
                              ? () => {
                                  if (!component || !courseId || !contentId) {
                                    return;
                                  }

                                  downloadDetailedScoresMutation.mutate({
                                    component,
                                    componentId: contentId,
                                    courseId,
                                  });
                                }
                              : undefined
                          }
                          onToggleHideStats={() =>
                            setHideStats((current) => !current)
                          }
                          onUpdateStats={
                            canEditContent
                              ? () => {
                                  if (!component || !courseId || !contentId) {
                                    return;
                                  }

                                  updateStatsMutation.mutate({
                                    component,
                                    componentId: contentId,
                                    courseId,
                                  });
                                }
                              : undefined
                          }
                          onRegrade={
                            canEditContent
                              ? () => setIsRegradeDialogOpen(true)
                              : undefined
                          }
                          showDownloadButton={
                            canEditContent &&
                            Boolean(
                              component?.questionSections.some(
                                (section) =>
                                  section.kind === 'homework' &&
                                  section.questions.some(
                                    (question) => question.isAssigned,
                                  ),
                              ),
                            )
                          }
                          showRegradeButton={canEditContent}
                          showUpdateButton={canEditContent}
                          updateSucceeded={
                            canEditContent && updateStatsMutation.isSuccess
                          }
                        />
                      ) : null}
                      {canEditContent &&
                      isRegradeDialogOpen &&
                      component &&
                      courseId &&
                      contentId ? (
                        <RegradeQuestionsDialog
                          component={component}
                          componentId={contentId}
                          courseId={courseId}
                          onOpenChange={setIsRegradeDialogOpen}
                          open={isRegradeDialogOpen}
                        />
                      ) : null}

                      {showInstructorClassSummary ? (
                        <ClassStatsSummary
                          classAveragePreLectureScore={
                            component.classAveragePreLectureScore
                          }
                          classAverageScore={component.classAverageScore}
                          maxScore={component.scoreSummary.homeworkMaxScore}
                          questionMaxScores={assignedHomeworkQuestions.map(
                            (question) => question.pointsPossible,
                          )}
                          questionScores={component.classQuestionScores}
                        />
                      ) : null}

                      {showStudentReviewSummary ? (
                        canEditContent ? null : (
                          <ScoreSummaryBanner scoreSummary={component.scoreSummary} />
                        )
                      ) : null}

                      {renderedQuestionSections.map((section, index) => {
                        const sectionState = getQuestionSectionState(section.kind);
                        const showHomeworkNotice =
                          section.kind === 'preLecture' &&
                          renderedQuestionSections.some(
                            (candidate) => candidate.kind === 'homework',
                          );

                        return (
                          <QuestionSection
                            areSolutionsVisible={sectionState.areSolutionsVisible}
                            anchorId={getQuestionSectionAnchorId(index)}
                            className={styles.panel}
                            chapterTitle={component.title}
                            componentId={contentId}
                            courseId={courseId}
                            courseSubject={courseQuery.data?.parentSubject}
                            dueStatusText={getQuestionSectionDueStatus(
                              section.kind,
                              timeline,
                            )}
                            hideStats={hideStats}
                            isPageMultiSelectActive={isMultiSelectActive}
                            isSectionClosed={sectionState.isClosed}
                            isSectionOpen={sectionState.isOpen}
                            onBeforeBulkAction={finishActiveEditBeforeBulkAction}
                            onBeforeEdit={clearMultiSelect}
                            onMultiSelectActiveChange={handleMultiSelectActiveChange}
                            onCopyQuestions={(scope, questions) => {
                              setCopyQuestionTarget({ questions, scope });
                            }}
                            key={`${section.kind}-${section.id}`}
                            section={section}
                            questionSections={renderedQuestionSections}
                            structuralEditsRestricted={getQuestionSectionRestriction(
                              section.kind,
                            )}
                            transitionNotice={
                              showHomeworkNotice
                                ? 'Homework questions are below.'
                                : undefined
                            }
                            userRole={userRole}
                          />
                        );
                      })}
                    </>
                  ) : component.questionSections.length > 0 ? (
                    <Card as="div" className={styles.panel} id={QUESTION_SECTION_ID}>
                      <h2>Questions</h2>
                      <p>Questions will appear when their section becomes available.</p>
                    </Card>
                  ) : (
                    <Card as="div" className={styles.panel} id={QUESTION_SECTION_ID}>
                      <h2>Questions</h2>
                      <p>This chapter has no questions yet.</p>
                    </Card>
                  )}
                </div>

                {hasNotesLane ? (
                  <div className={styles.rightSidebar} data-content-notes-lane="true">
                    {homeworkDueCard}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* Always there, even empty: the page keeps the room it leaves the
              dock while the assistant takes its place. */}
          <div
            className={styles.floatingDock}
            data-edit-session={showEditSessionBar ? 'true' : 'false'}
            ref={floatingDockRef}
          >
            {hasFloatingActions ? (
              <div className={styles.floatingActionStack}>
                {showEditContentFab ? (
                  <button
                    aria-label="Edit content"
                    className={styles.editContentFab}
                    onClick={() => {
                      contentEditMode.openEditMode();
                    }}
                    type="button"
                  >
                    <span className={styles.fabButton}>
                      <Pencil aria-hidden="true" size={22} strokeWidth={2.4} />
                    </span>
                    <span aria-hidden="true" className={styles.fabLabel}>
                      Edit Content
                    </span>
                  </button>
                ) : null}

                {component && courseId && contentId ? (
                  <div className={styles.floatingActionItem}>
                    <ChapterActionsMenu
                      annotationAvailable={isAnnotationAvailable}
                      annotationsOpen={isAnnotationsOpen}
                      onExportPdf={() => setIsPdfExportDialogOpen(true)}
                      onToggleAnnotations={openAnnotationsPanel}
                      ref={annotationToggleRef}
                    />
                    <span aria-hidden="true" className={styles.fabLabel}>
                      More
                    </span>
                  </div>
                ) : null}

                {isChatAvailable ? (
                  <div className={styles.floatingActionItem}>
                    <ChatbotAvatar
                      aria-label="Open AI study assistant"
                      className={styles.chatAvatarAnchor}
                      onClick={toggleChatPanel}
                      ref={chatAvatarRef}
                      {...avatarState}
                    />
                    <span aria-hidden="true" className={styles.fabLabel}>
                      Ask KAI
                    </span>
                  </div>
                ) : null}
              </div>
            ) : null}

            {showEditSessionBar ? (
              <div
                aria-label="Edit session actions"
                className={styles.editSessionBar}
                data-active-unit={contentEditMode.isAnyUnitActive ? 'true' : 'false'}
                role="region"
              >
                <button
                  className={styles.editSessionCancel}
                  onClick={handlePageLevelCancel}
                  type="button"
                >
                  {hasPendingEdits ? 'Cancel' : 'Exit'}
                </button>
                <button
                  aria-disabled={isPageSaveDisabled ? 'true' : undefined}
                  className={styles.editSessionSave}
                  disabled={isPageSaveDisabled}
                  onClick={() => {
                    void handlePageLevelSave();
                  }}
                  ref={pageSaveButtonRef}
                  type="button"
                >
                  {isPageSaving ? 'Saving...' : 'Save'}
                </button>
                <p
                  aria-atomic="true"
                  aria-live="polite"
                  className={styles.editSessionStatus}
                  data-tone={pageSaveStatusTone}
                  ref={editSessionStatusRef}
                  tabIndex={-1}
                >
                  {pageSaveStatus}
                </p>
              </div>
            ) : null}
          </div>

          <QuestionEditDialog
            disabled={contentEditMode.editor.isSaving || saveComponent.isPending}
            focusFallback={findLastEditedQuestionButton}
            mode={contentEditMode.activeQuestion?.isNew ? 'new' : 'edit'}
            onOpenChange={(nextOpen) => {
              if (!nextOpen) {
                contentEditMode.cancelActiveQuestion();
              }
            }}
            onSave={contentEditMode.saveActiveQuestion}
            open={Boolean(
              contentEditMode.activeQuestionEditTarget &&
                contentEditMode.activeQuestion,
            )}
            question={contentEditMode.activeQuestion}
            questionLabel={contentEditMode.activeQuestionLabel}
            structuralEditsRestricted={activeQuestionStructuralEditsRestricted}
          />

          {copySectionTargets.length > 0 &&
          component?.rawComponent &&
          courseId &&
          contentId ? (
            <CopySectionDialog
              currentComponent={component.rawComponent}
              currentComponentHasUnsavedChanges={contentEditMode.hasUnsavedChanges}
              currentComponentId={contentId}
              currentComponentTitle={component.title ?? 'Current chapter'}
              currentCourseId={courseId}
              currentCourseTitle={courseQuery.data?.title ?? 'Current course'}
              onOpenChange={(open) => {
                if (!open) {
                  setCopySectionTargets([]);
                }
              }}
              onSuccess={handleContentCopySuccess}
              open={copySectionTargets.length > 0}
              sections={copySectionTargets}
            />
          ) : null}

          {component?.rawComponent && courseId && contentId && copyQuestionTarget ? (
            <CopyQuestionDialog
              currentComponent={component.rawComponent}
              currentComponentHasUnsavedChanges={contentEditMode.hasUnsavedChanges}
              currentComponentId={contentId}
              currentComponentTitle={component.title ?? 'Current chapter'}
              currentCourseId={courseId}
              currentCourseTitle={courseQuery.data?.title ?? 'Current course'}
              onOpenChange={(open) => {
                if (!open) {
                  setCopyQuestionTarget(null);
                }
              }}
              onSuccess={handleQuestionCopySuccess}
              open={Boolean(copyQuestionTarget)}
              questions={copyQuestionTarget.questions}
              scope={copyQuestionTarget.scope}
            />
          ) : null}

          {component && printableChapter && courseId && contentId ? (
            <ExportChapterPdfDialog
              chapterTitle={component.title}
              componentId={contentId}
              courseId={courseId}
              courseTitle={courseQuery.data?.title ?? 'Current course'}
              hasQuestions={printableChapter.questionSections.length > 0}
              hasReading={printableChapter.contentSections.some(
                (section) => section.units.length > 0,
              )}
              hasVideo={printableChapter.videoLinks.length > 0}
              onOpenChange={setIsPdfExportDialogOpen}
              open={isPdfExportDialogOpen}
            />
          ) : null}

          {isAnnotationAvailable ? (
            <AnnotationsSidebar
              error={annotationSession.error}
              filter={annotationSession.filter}
              groups={annotationGroups}
              highlightColor={annotationSession.highlightColor}
              noteTextColor={annotationSession.noteTextColor}
              onClearError={annotationSession.clearError}
              onClose={() => setActiveStudentPanel(null)}
              onDeleteAnnotation={async (annotation) => {
                await annotationSession.deleteAnnotation({
                  annotation,
                  contentUnitId: annotation.contentUnitId,
                });
              }}
              onFilterChange={annotationSession.setFilter}
              onHighlightColorChange={annotationSession.setHighlightColor}
              onNoteTextColorChange={(color) => {
                void handleNoteTextColorChange(color);
              }}
              onSelectAnnotation={handleAnnotationNavigation}
              onShowAnnotationsChange={annotationSession.setShowAnnotations}
              open={isAnnotationsOpen}
              showAnnotations={annotationSession.showAnnotations}
            />
          ) : null}

          {isChatAvailable ? (
            <>
              {shouldRenderChatPanel ? (
                <ChatPanel
                  chapterImageUrls={chapterImageUrls}
                  avatar={
                    <ChatbotAvatar
                      aria-label="AI study assistant avatar"
                      decorative
                      size={40}
                      {...avatarState}
                    />
                  }
                  isClosing={isChatClosing}
                  onClose={closeChatPanel}
                  onTypingChange={setIsUserTypingInChat}
                  session={chatSession}
                />
              ) : null}
            </>
          ) : null}
        </>
      ) : null}
      </section>
    </ContentEditModeProvider>
    </ImageSizeOverrideProvider>
  );
}
